import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Check } from "lucide-react";
import { SERVICES } from "@/lib/site";

const TABS = [
  { id: "civile", label: "Pulizie civili", img: "/img/civile.jpg", note: "Case, uffici, condomini, divani e materassi." },
  { id: "industriale", label: "Pulizie industriali", img: "/img/magazzino.jpg", note: "Capannoni, reparti produttivi, sanificazione e pest control." },
  { id: "tutti", label: "Tutti", img: "/img/uffici.jpg", note: "Combina servizi civili e industriali." },
];

const ServiceRow = ({ s, index, active, onToggle, onHover }) => (
  <motion.button
    layout
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.1 }}
    transition={{ duration: 0.6, delay: index * 0.05, ease: [0.22, 1, 0.36, 1] }}
    onClick={() => onToggle(s.id)}
    onMouseEnter={() => onHover(s)}
    onMouseLeave={() => onHover(null)}
    onFocus={() => onHover(s)}
    onBlur={() => onHover(null)}
    data-testid={`service-card-${s.id}`}
    aria-pressed={active}
    className={`group relative flex w-full items-start gap-5 border-b border-white/10 py-6 text-left transition-colors duration-300 sm:gap-8 ${active ? "bg-[var(--cyan)]/[0.06]" : "hover:bg-white/[0.03]"}`}
  >
    <span className={`absolute left-0 top-0 h-full w-[3px] origin-top bg-[var(--cyan)] transition-transform duration-500 ${active ? "scale-y-100" : "scale-y-0"}`} />
    <span className="w-10 shrink-0 pl-4 pt-1 font-mono text-xs text-slate-400">{String(index + 1).padStart(2, "0")}</span>
    {s.img && (
      <span className="hidden h-20 w-20 shrink-0 overflow-hidden rounded-2xl border border-white/10 sm:block" data-testid={`service-thumb-${s.id}`}>
        <img src={s.img} alt={s.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
      </span>
    )}
    <span className="flex-1">
      <span className="flex flex-wrap items-center gap-3">
        <span className="font-display text-xl font-semibold text-white transition-transform duration-300 group-hover:translate-x-1 sm:text-2xl">{s.title}</span>
        <span className="rounded-full border border-white/15 px-2.5 py-0.5 font-mono text-[11px] uppercase tracking-wider text-slate-400">{s.cat}</span>
      </span>
      <span className="mt-2 block max-w-xl text-sm text-slate-400 sm:text-base">{s.desc}</span>
    </span>
    <span
      data-testid={`service-checkbox-${s.id}`}
      className={`mr-4 mt-1 grid h-11 w-11 shrink-0 place-items-center rounded-full border transition-[background-color,border-color,transform] duration-300 group-active:scale-90 ${active ? "border-[var(--cyan)] bg-[var(--cyan)] text-black" : "border-white/20 text-white group-hover:border-[var(--cyan)]"}`}
    >
      {active ? <Check className="h-5 w-5" /> : <Plus className="h-5 w-5 transition-transform duration-300 group-hover:rotate-90" />}
    </span>
  </motion.button>
);

export const ServiceMenu = ({ selected, onToggle }) => {
  const [tab, setTab] = useState("civile");
  const [hovered, setHovered] = useState(null);
  const current = TABS.find((t) => t.id === tab);
  const panel = hovered?.img ? { img: hovered.img, label: hovered.title, note: hovered.title } : current;
  const list = tab === "tutti" ? SERVICES : SERVICES.filter((s) => s.cat === tab);
  return (
    <section id="servizi" className="relative py-24 sm:py-32">
      <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-[0.9fr_1.4fr]">
        <div className="lg:sticky lg:top-28 lg:self-start">
          <p className="eyebrow">Il menu · scegli tu</p>
          <h2 className="mt-5 font-display text-4xl font-bold leading-[1] tracking-tight text-white sm:text-5xl">
            Componi la tua <span className="text-[var(--cyan)]">pulizia</span>.
          </h2>
          <p className="mt-5 max-w-md text-base text-slate-300">Tocca i servizi che ti interessano: li ritrovi già pronti nella richiesta di preventivo o nel messaggio WhatsApp.</p>
          <div className="relative mt-8 aspect-[4/3] overflow-hidden rounded-[28px] border border-white/10 lg:aspect-[4/5]">
            <AnimatePresence initial={false}>
              <motion.img
                key={panel.img}
                src={panel.img}
                alt={panel.label}
                initial={{ opacity: 0, scale: 1.12 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                className="absolute inset-0 h-full w-full object-cover"
              />
            </AnimatePresence>
            <div className="absolute inset-0 bg-gradient-to-t from-[#070B14]/90 via-transparent" />
            <p className="absolute bottom-5 left-5 right-5 font-display text-lg font-semibold text-white" data-testid="service-panel-note">{panel.note}</p>
          </div>
        </div>

        <div>
          <div className="flex flex-wrap gap-2 rounded-full border border-white/10 bg-white/[0.03] p-1.5" role="tablist">
            {TABS.map((t) => (
              <button key={t.id} role="tab" aria-selected={tab === t.id} data-testid={`tab-${t.id}`} onClick={() => setTab(t.id)}
                className={`relative flex-1 rounded-full px-4 py-2.5 text-sm font-semibold transition-colors ${tab === t.id ? "text-black" : "text-slate-300 hover:text-white"}`}>
                {tab === t.id && <motion.span layoutId="tab-pill" className="absolute inset-0 rounded-full bg-[var(--cyan)]" transition={{ type: "spring", stiffness: 380, damping: 30 }} />}
                <span className="relative">{t.label}</span>
              </button>
            ))}
          </div>
          <div className="mt-6 border-t border-white/10" data-testid="service-list">
            {list.map((s, i) => (
              <ServiceRow key={s.id} s={s} index={i} active={selected.includes(s.id)} onToggle={onToggle} onHover={setHovered} />
            ))}
          </div>
          <p className="mt-6 font-mono text-xs uppercase tracking-[0.2em] text-slate-400" data-testid="service-selected-count">
            {selected.length} {selected.length === 1 ? "servizio selezionato" : "servizi selezionati"}
          </p>
        </div>
      </div>
    </section>
  );
};
